import React from "react";
import { Button, Col, Form, Row } from "react-bootstrap";

const columnKey = (columns) => {
  let index = columns.length + 1;
  let key = `column_${index}`;
  const keys = new Set(columns.map((column) => column.key));

  while (keys.has(key)) {
    index += 1;
    key = `column_${index}`;
  }

  return key;
};

const emptyColumn = (columns = []) => {
  const key = columnKey(columns);
  return {
    key,
    label: `Column ${columns.length + 1}`,
    type: "text",
    align: "left",
  };
};

const emptyRow = (columns) =>
  columns.reduce((row, column) => {
    row[column.key] = column.type === "boolean" ? false : "";
    return row;
  }, {});

export const emptyTable = (order = 0) => {
  const columns = [emptyColumn()];
  return {
    title: "",
    subtitle: "",
    source: "",
    order,
    columns,
    rows: [emptyRow(columns)],
  };
};

const normalizeTable = (table, index) => {
  const columns = Array.isArray(table?.columns) && table.columns.length > 0
    ? table.columns
    : [emptyColumn()];

  return {
    ...emptyTable(index + 1),
    ...table,
    columns,
    rows: Array.isArray(table?.rows) && table.rows.length > 0
      ? table.rows
      : [emptyRow(columns)],
  };
};

const hasValue = (value) =>
  value === false || value === 0 || (value !== null && value !== undefined && String(value).trim() !== "");

export const cleanTables = (tables = []) =>
  tables
    .map((table, index) => {
      const columns = (table.columns || []).map((column, columnIndex) => ({
        key: column.key || `column_${columnIndex + 1}`,
        label: column.label?.trim() || `Column ${columnIndex + 1}`,
        type: ["number", "boolean", "date"].includes(column.type)
          ? column.type
          : "text",
        align: ["center", "right"].includes(column.align) ? column.align : "left",
      }));

      return {
        ...table,
        title: table.title?.trim() || "",
        subtitle: table.subtitle?.trim() || "",
        source: table.source?.trim() || "",
        order: index + 1,
        columns,
        rows: (table.rows || [])
          .filter((row) => columns.some((column) => hasValue(row[column.key])))
          .map((row) =>
            columns.reduce((nextRow, column) => {
              const value = row[column.key];
              if (column.type === "number") {
                nextRow[column.key] = value === "" ? "" : Number(value);
              } else if (column.type === "boolean") {
                nextRow[column.key] = Boolean(value);
              } else {
                nextRow[column.key] = value ?? "";
              }
              return nextRow;
            }, {})
          ),
      };
    })
    .filter((table) => table.title || table.rows.length > 0);

const BlogTableBuilder = ({ tables = [], onChange, compact = false }) => {
  const updateTables = (nextTables) => {
    onChange(nextTables.map((table, index) => ({ ...table, order: index + 1 })));
  };

  const updateTable = (tableIndex, updater) => {
    updateTables(
      tables.map((table, index) =>
        index === tableIndex ? updater(normalizeTable(table, tableIndex)) : table
      )
    );
  };

  const handleTableChange = (tableIndex, field, value) => {
    updateTable(tableIndex, (table) => ({ ...table, [field]: value }));
  };

  const handleColumnChange = (tableIndex, columnIndex, field, value) => {
    updateTable(tableIndex, (table) => {
      const previousColumn = table.columns[columnIndex];
      const columns = table.columns.map((column, index) =>
        index === columnIndex ? { ...column, [field]: value } : column
      );

      if (field !== "type" || previousColumn.type === value) {
        return { ...table, columns };
      }

      const rows = table.rows.map((row) => ({
        ...row,
        [previousColumn.key]: value === "boolean" ? Boolean(row[previousColumn.key]) : row[previousColumn.key] ?? "",
      }));
      return { ...table, columns, rows };
    });
  };

  const handleCellChange = (tableIndex, rowIndex, key, value) => {
    updateTable(tableIndex, (table) => ({
      ...table,
      rows: table.rows.map((row, index) =>
        index === rowIndex ? { ...row, [key]: value } : row
      ),
    }));
  };

  const addColumn = (tableIndex) => {
    updateTable(tableIndex, (table) => {
      const nextColumn = emptyColumn(table.columns);
      return {
        ...table,
        columns: [...table.columns, nextColumn],
        rows: table.rows.map((row) => ({ ...row, [nextColumn.key]: "" })),
      };
    });
  };

  const removeColumn = (tableIndex, columnIndex) => {
    updateTable(tableIndex, (table) => {
      if (table.columns.length === 1) return table;
      const removedColumn = table.columns[columnIndex];
      const rows = table.rows.map((row) => {
        const nextRow = { ...row };
        delete nextRow[removedColumn.key];
        return nextRow;
      });
      return {
        ...table,
        columns: table.columns.filter((_, index) => index !== columnIndex),
        rows,
      };
    });
  };

  const addRow = (tableIndex) => {
    updateTable(tableIndex, (table) => ({
      ...table,
      rows: [...table.rows, emptyRow(table.columns)],
    }));
  };

  const removeRow = (tableIndex, rowIndex) => {
    updateTable(tableIndex, (table) => ({
      ...table,
      rows: table.rows.length === 1
        ? table.rows
        : table.rows.filter((_, index) => index !== rowIndex),
    }));
  };

  return (
    <div className={compact ? "" : "border-top pt-3 mt-3"}>
      {!compact && (
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="mb-0">Tables</h5>
          <Button
            variant="outline-primary"
            type="button"
            onClick={() => updateTables([...tables, emptyTable(tables.length + 1)])}
          >
            Add Table
          </Button>
        </div>
      )}

      {!compact && tables.length === 0 && (
        <p className="text-muted mb-3">No table blocks added.</p>
      )}

      {tables.map((table, tableIndex) => {
        const normalizedTable = normalizeTable(table, tableIndex);

        return (
          <div className="border rounded p-3 mb-3" key={table.id || `table-${tableIndex}`}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    value={normalizedTable.title || ""}
                    onChange={(event) => handleTableChange(tableIndex, "title", event.target.value)}
                    placeholder="Table title"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Subtitle</Form.Label>
                  <Form.Control
                    value={normalizedTable.subtitle || ""}
                    onChange={(event) => handleTableChange(tableIndex, "subtitle", event.target.value)}
                    placeholder="Optional subtitle"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Source</Form.Label>
              <Form.Control
                value={normalizedTable.source || ""}
                onChange={(event) => handleTableChange(tableIndex, "source", event.target.value)}
                placeholder="Optional data source"
              />
            </Form.Group>

            <div className="d-flex justify-content-between align-items-center mb-2">
              <b>Columns</b>
              <Button variant="outline-secondary" size="sm" type="button" onClick={() => addColumn(tableIndex)}>
                Add Column
              </Button>
            </div>
            {normalizedTable.columns.map((column, columnIndex) => (
              <Row className="align-items-end mb-2" key={column.key}>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Column Name</Form.Label>
                    <Form.Control
                      value={column.label || ""}
                      onChange={(event) => handleColumnChange(tableIndex, columnIndex, "label", event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Type</Form.Label>
                    <Form.Select
                      value={column.type || "text"}
                      onChange={(event) => handleColumnChange(tableIndex, columnIndex, "type", event.target.value)}
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="boolean">Yes / No</option>
                      <option value="date">Date</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Alignment</Form.Label>
                    <Form.Select
                      value={column.align || "left"}
                      onChange={(event) => handleColumnChange(tableIndex, columnIndex, "align", event.target.value)}
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Button
                    variant="outline-danger"
                    className="w-100"
                    type="button"
                    disabled={normalizedTable.columns.length === 1}
                    onClick={() => removeColumn(tableIndex, columnIndex)}
                  >
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}

            <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
              <b>Rows</b>
              <Button variant="outline-secondary" size="sm" type="button" onClick={() => addRow(tableIndex)}>
                Add Row
              </Button>
            </div>
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-2">
                <thead>
                  <tr>
                    {normalizedTable.columns.map((column) => <th key={column.key}>{column.label}</th>)}
                    <th style={{ width: 100 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {normalizedTable.rows.map((row, rowIndex) => (
                    <tr key={`row-${rowIndex}`}>
                      {normalizedTable.columns.map((column) => (
                        <td key={column.key}>
                          {column.type === "boolean" ? (
                            <Form.Check
                              type="checkbox"
                              checked={Boolean(row[column.key])}
                              onChange={(event) => handleCellChange(tableIndex, rowIndex, column.key, event.target.checked)}
                              label={row[column.key] ? "Yes" : "No"}
                            />
                          ) : (
                            <Form.Control
                              type={column.type === "number" ? "number" : column.type === "date" ? "date" : "text"}
                              value={row[column.key] ?? ""}
                              onChange={(event) => handleCellChange(tableIndex, rowIndex, column.key, event.target.value)}
                            />
                          )}
                        </td>
                      ))}
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          type="button"
                          disabled={normalizedTable.rows.length === 1}
                          onClick={() => removeRow(tableIndex, rowIndex)}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!compact && (
              <div className="text-end mt-3">
                <Button
                  variant="outline-danger"
                  type="button"
                  onClick={() => updateTables(tables.filter((_, index) => index !== tableIndex))}
                >
                  Remove Table
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BlogTableBuilder;
